import { DispatchSelection } from 'src/components/dispatch/detailPanel/DispatchDetailPanel';
import { scopeGroupsToVehicle, vehicleScopeKey } from 'src/components/dispatch/vehicleRowScope';
import { DispatchCard } from 'src/entities/dispatch';

import { VehicleRowGroup } from './adapter';

/**
 * 座標が変わっても変わらない同一性で「同じ停車地のカードか」を判定する。
 *
 * legRunIds を使うのは、legRun の同一性が伝票（shipment_order_leg_run）由来で、
 * 座標・地点名を書き換えても id が変わらないため。カード id は使えない:
 * 積地は `loading-${truckId}-${rotation}-${placeKey}-${index}`、固定ルート template は
 * `template-${truckId}-${rotation}-${cardIndex}-${placeKey}` で、placeKey は
 * `${lat}-${lng}`（vehicleRowVM.ts の buildPlaceKey）。座標更新そのもので id が変わる。
 *
 * 同じ legRun は積地カードと降地カードの両方に載るので type を、
 * 降地は matched / template / fr-template / no-delivery が別カードとして併存しうるので
 * status も併せて見る。
 */
function isSameStop(a: DispatchCard, b: DispatchCard): boolean {
  if (a.type !== b.type) return false;
  if (a.type === 'delivery' && b.type === 'delivery' && a.status !== b.status) return false;
  const aIds = a.legRunIds ?? [];
  const bIds = b.legRunIds ?? [];
  // legRun と紐付かないカード（本日なし等）は引き直せない。座標編集の対象にもならない
  if (aIds.length === 0 || bIds.length === 0) return false;
  return aIds.some((id) => bIds.includes(id));
}

/**
 * 選択中の車両カードを、いま表示している rowGroups の同 id のカードへ解決し直す。
 *
 * 詳細パネルはクリック時のカードオブジェクトを state に持つ。座標更新のように
 * 「サーバへ反映 → キャッシュ invalidate → 再取得」で中身だけが変わる操作は、
 * 選択を差し替えないとパネルだけ古い値を出し続ける。成功時に選択中のカードだけ
 * 手で差し替えるより、常に最新から引き直すほうが経路が増えても崩れない。
 *
 * 見つからないとき (削除された・非表示 rotation になった・別 scope のデータが来た) は
 * 元の選択をそのまま返す。null を返すとパネルが勝手に閉じてしまう。
 *
 * Why not orderIndex も引き直す: orderIndex はクリックした訪問そのものを指す識別子で、
 * /map のトグル判定 (isSameDetailTarget) が「同じ訪問の再クリックか」に使っている。
 * 引き直して別の値になるとトグルが壊れる。
 *
 * Why not 未割当も引き直す: 未割当リストはこの解決の入力に無い (hook が持つのは
 * rowGroups と legRuns)。座標編集は未割当では常に不可 (geoEditSide が null) なので、
 * 指摘の対象になる経路も無い。
 */
/**
 * 選択の同一性キー。「応答待ちの間に別の詳細へ切り替わっていないか」の照合と、
 * /map のトグル判定（isSameDetailTarget）の両方が使う。
 *
 * カード側は id だけでなく orderIndex も含める（同一地点への再訪は別の選択として扱う）。
 * さらに**勤務**も含める: 積地カードの id は勤務をまたいで衝突し、積地の orderIndex は
 * 常に 0 なので、勤務を入れないと 1 台に複数勤務が乗るときに別勤務の積地を同じ対象と
 * 誤認する (logposeTech/log_web_issues#320。vehicleScopeKey の Why 参照)。
 */
export function selectionKey(selection: DispatchSelection): string | null {
  if (!selection) return null;
  if (selection.source === 'unassigned') return `unassigned:${selection.item.id}`;
  return `vehicle:${vehicleScopeKey(selection.vehicle)}:${selection.card.id}:${selection.orderIndex}`;
}

/**
 * 同じ対象を指しているか（/map のクリック入口のトグル判定）。
 * カード配車は resolveCardClick が選択カードと詳細をまとめて決めるが、/map には
 * 選択カードの概念が無いので詳細の開閉だけを見る。
 *
 * 判定は selectionKey に委譲する。「応答待ちの照合」と「トグル」で粒度が割れると、
 * 一方だけ別勤務を取り違える (#320 の指摘がまさにこれ)。
 *
 * Why not: unassigned 同士の比較は持たない。この関数を使う openDetailFromClick の
 * 呼び出し元は停車地クリックと地図マーカークリックの 2 つだけで、next は必ず 'vehicle'
 * か null になる。未割当ピンのトグルはピン留め id (pinnedUnassignedIdRef) で判定する。
 */
export function isSameDetailTarget(current: DispatchSelection, next: DispatchSelection): boolean {
  if (!current || !next) return false;
  if (current.source !== 'vehicle' || next.source !== 'vehicle') return false;
  return selectionKey(current) === selectionKey(next);
}

export function resolveSelectionFromLatest(
  selection: DispatchSelection,
  rowGroups: VehicleRowGroup[],
): DispatchSelection {
  if (!selection || selection.source !== 'vehicle') return selection;

  // 探索は選択していた勤務に閉じる。別車両へ移った案件まで追いかけてパネルが勝手に
  // 別の車両へ飛ばないようにするためと、**積地カードの id は勤務をまたいで衝突する**ため
  // (logposeTech/log_web_issues#320。scopeGroupsToVehicle の Why 参照)。
  // id で引けないのは座標更新で id 自体が変わった場合（isSameStop 参照）。
  const scoped = scopeGroupsToVehicle(selection.vehicle, rowGroups);
  const found = findById(selection.card.id, scoped) ?? findSameStop(selection.card, scoped);
  if (!found) return selection;

  // 中身が変わっていなければ参照を保つ。毎 render で新しい選択を作ると、
  // これを依存に持つハンドラ・memo が無駄に作り直される
  if (found.card === selection.card && found.vehicle === selection.vehicle) return selection;
  return { ...selection, card: found.card, vehicle: found.vehicle };
}

type FoundCard = { card: DispatchCard; vehicle: VehicleRowGroup['vehicle'] };

function findById(cardId: string, rowGroups: readonly VehicleRowGroup[]): FoundCard | null {
  for (const group of rowGroups) {
    for (const row of group.rows) {
      const card = row.cards.find((c) => c.id === cardId);
      if (card) return { card, vehicle: group.vehicle };
    }
  }
  return null;
}

/**
 * 同じ停車を探す。**勤務で絞り込み済みのグループ**を受け取る前提
 * （絞り込みは scopeGroupsToVehicle。1 台に複数のドライバーが乗る勤務では vehicle.id が
 * truck 単位で重複するため、勤務が分かるときは shiftTruckId で引く）。
 */
function findSameStop(target: DispatchCard, rowGroups: readonly VehicleRowGroup[]): FoundCard | null {
  for (const group of rowGroups) {
    for (const row of group.rows) {
      const card = row.cards.find((c) => isSameStop(c, target));
      if (card) return { card, vehicle: group.vehicle };
    }
  }
  return null;
}
